import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  SortAscendingOutlined,
  SortDescendingOutlined
} from '@ant-design/icons'
import { ConfigProvider, Select, Typography } from 'antd'
import clsx from 'clsx'
import useDirectory from 'hooks/useDirectory'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingFilesState,
  listPointState,
  revalidateFileListState
} from 'store/buildingStore'
import useSWR from 'swr'
import {
  BuildingChildrenFolder,
  BuildingFile,
  Folders,
  ListBuildingFiles
} from 'types/building'

import Button from 'components/atoms/Button'
import Spinner from 'components/atoms/Spinner'
import AddFolderIcon from 'components/icons/AddFolderIcon'
import LeftArrowIcon from 'components/icons/LeftArrowIcon'
import MoreIcon from 'components/icons/MoreIcon'

import Image360Detail from './360ImageDetail'
import BuildingFileDetail from './BuildingFileDetail'
import ChildrenFolderList from './ChildrenFolderList'
import UploadFileMenu from './MenuUploadFile'
import ModalAddOrEditChildrenFolder from './ModalAddOrEditChildrenFolder'
import ModalDelete360Image from './ModalDelete360Image'
import ModalDeleteFile from './ModalDeleteFile'

type Props = {
  handleBack: (() => void) | undefined
  selectedFolder: { doc: Folders; subDoc?: Folders } | undefined
}

export default function MenuInstruction({ handleBack, selectedFolder }: Props) {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const { getBuiildingType } = useDirectory()

  const [selectImage, setSelectImage] = useState<number>()
  const [uploadFileStep, setUploadFileStep] = useState(false)
  const [isAddingFolder, setIsAddingFolder] = useState(false)
  const [deleteFile, setDeleteFile] = useState<BuildingFile | undefined>(
    undefined
  )
  const [confirmDelete, setConfirmDelete] = useState<
    | {
        id: number
        name: string
      }
    | undefined
  >()
  const [selectedChildrenFolder, setSelectedChildrenFolder] = useState<number>()
  const [previewFileIndex, setPreviewFileIndex] = useState<number | undefined>()
  const [sortBy, setSortBy] = useState<'name' | 'createdAt'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [renderedPage, setRenderedPage] = useState(0)
  const [allFiles, setAllFiles] = useState<BuildingFile[]>([])
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isRequestingNextPageRef = useRef(false)

  const points = useRecoilValue(listPointState)
  const [buildingFiles, setBuildingFiles] = useRecoilState(buildingFilesState)
  const [revalidateFileList, setRevalidateFileList] = useRecoilState(
    revalidateFileListState
  )

  const sorted360Images = useMemo(
    () =>
      buildingFiles.files.map((file) => {
        const pointInFloor = points.find(
          (point) => point.id === file.pointId || point.image360?.id === file.id
        )
        // file is used in other floor if file tied to a point and that point is not current floor
        const isInFloor = Boolean(!floorId || pointInFloor || !file.pointId)
        return {
          ...file,
          isInFloor,
          pointOrder: pointInFloor?.order || 0
        }
      }),
    [buildingFiles, floorId, points]
  )

  const sortedBuildingFiles = useMemo(() => {
    const list = [...buildingFiles.files]
    return list.sort((a, b) => {
      const fieldA = sortBy === 'name' ? a.name : a.createdAt
      const fieldB = sortBy === 'name' ? b.name : b.createdAt

      if (sortOrder === 'asc') {
        return String(fieldA).localeCompare(String(fieldB), 'ja', {
          numeric: true
        })
      }
      return String(fieldB).localeCompare(String(fieldA), 'ja', {
        numeric: true
      })
    })
  }, [buildingFiles, sortBy, sortOrder])

  const sortedCondition = useMemo(
    () =>
      selectedFolder && selectedFolder.doc.isFileHolder
        ? selectedFolder.doc.id
        : selectedFolder?.subDoc?.categoryId,
    [selectedFolder]
  )

  const is360Tab = sortedCondition === 1

  const { data, isValidating, mutate, error } = useSWR<ListBuildingFiles>(
    {
      url: 'v1/buildings/files',
      params: {
        buildingId: id,
        floorId,
        category: sortedCondition,
        page,
        pageSize: 20,
        order: is360Tab ? 'position' : sortBy,
        direction: is360Tab ? 'asc' : sortOrder
      }
    },
    { errorRetryCount: 3 }
  )

  const hasMore = Boolean(
    data?.pagination && allFiles.length < data.pagination.total
  )

  const { data: allBuildingFiles, mutate: mutateAllBuildingFiles } =
    useSWR<ListBuildingFiles>({
      url: 'v1/buildings/files',
      params: {
        buildingId: id,
        category: sortedCondition
      }
    })

  const {
    data: childrenFolderFiles,
    isValidating: isValidatingChildrenFolderFiles,
    mutate: mutateChildrenFolder
  } = useSWR<BuildingChildrenFolder>(
    selectedChildrenFolder
      ? {
          url: `v1/buildings/${id}/folders/${selectedChildrenFolder}`
        }
      : null
  )

  const sortedChildrenFolderFiles = useMemo(() => {
    if (!childrenFolderFiles?.files) return []
    const list = [...childrenFolderFiles.files]
    return list.sort((a: BuildingFile, b: BuildingFile) => {
      const fieldA = sortBy === 'name' ? a.name : a.createdAt
      const fieldB = sortBy === 'name' ? b.name : b.createdAt

      if (sortOrder === 'asc') {
        return String(fieldA).localeCompare(String(fieldB), 'ja', {
          numeric: true
        })
      }
      return String(fieldB).localeCompare(String(fieldA), 'ja', {
        numeric: true
      })
    })
  }, [childrenFolderFiles?.files, sortBy, sortOrder])

  const handleSwitchMenu = () => {
    setUploadFileStep((prev) => !prev)
  }

  const handleSelectImage = (id: number) => {
    if (selectImage === id) {
      setSelectImage(undefined)
      return
    }
    setSelectImage(id)
  }

  const handleToggleDeleteModal = (
    key: { id: number; name: string } | undefined
  ) => {
    setConfirmDelete(key)
  }

  const handleToggleDeleteFile = (file: BuildingFile | undefined) => {
    setDeleteFile(file)
  }

  const handleSetRevalidateByCategory = () => {
    setRevalidateFileList(selectedChildrenFolder || sortedCondition)
  }

  const handleMenuBack = () => {
    if (selectedChildrenFolder) {
      setSelectedChildrenFolder(undefined)
      return
    }
    handleBack && handleBack()
  }

  const handleOpenMenu = () => {
    if (!isValidatingChildrenFolderFiles) {
      setIsAddingFolder(true)
    }
  }

  const stateRef = useRef({
    isValidating,
    hasMore,
    page,
    renderedPage
  })
  stateRef.current = {
    isValidating,
    hasMore,
    page,
    renderedPage
  }

  useEffect(() => {
    if (error) {
      isRequestingNextPageRef.current = false
    }
  }, [error])

  useEffect(() => {
    setPage(1)
    setRenderedPage(0)
    setAllFiles([])
    isRequestingNextPageRef.current = false
  }, [floorId, sortedCondition, sortBy, sortOrder])

  useEffect(() => {
    if (!data?.files || isValidating) return

    const newFiles = data.files.files || []

    setAllFiles((prev) => {
      const existingIds = new Set(prev.map((f) => f.id))
      return page === 1
        ? newFiles
        : [...prev, ...newFiles.filter((f) => !existingIds.has(f.id))]
    })

    setBuildingFiles((prev) => {
      // Build a quick lookup map of existing local blob URLs (thumbnails
      // that were set optimistically after upload and haven't been replaced
      // by a real server thumbnail yet).
      const localBlobMap: Record<number, string> = {}
      prev.files.forEach((f) => {
        if (f.thumbnail?.startsWith('blob:')) {
          localBlobMap[f.id] = f.thumbnail
        }
      })

      const existingIds = new Set(prev.files.map((f) => f.id))
      const baseFiles =
        page === 1
          ? newFiles
          : [...prev.files, ...newFiles.filter((f) => !existingIds.has(f.id))]

      const mergedFiles = baseFiles.map((serverFile) => {
        // If the server hasn't generated the thumbnail yet but we
        // already have a local blob URL, keep showing it.
        if (
          (!serverFile.thumbnail ||
            serverFile.thumbnail.includes('/assets/no-image.svg')) &&
          localBlobMap[serverFile.id]
        ) {
          return { ...serverFile, thumbnail: localBlobMap[serverFile.id] }
        }
        return serverFile
      })

      return {
        files: mergedFiles,
        folders: data?.files.folders ?? prev.folders
      }
    })

    setRenderedPage(page)
    isRequestingNextPageRef.current = false
  }, [data, isValidating, page, setBuildingFiles])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || selectedChildrenFolder) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        const state = stateRef.current
        if (
          entry?.isIntersecting &&
          !state.isValidating &&
          state.hasMore &&
          state.renderedPage === state.page &&
          !isRequestingNextPageRef.current
        ) {
          isRequestingNextPageRef.current = true
          setPage((prev) => prev + 1)
        }
      },
      { root: sentinel.parentElement }
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, selectedChildrenFolder])

  useEffect(() => {
    if (revalidateFileList) {
      if (revalidateFileList === sortedCondition) {
        mutate()
        mutateAllBuildingFiles()
      }
      setRevalidateFileList(undefined)
    }
  }, [
    mutate,
    mutateAllBuildingFiles,
    revalidateFileList,
    setRevalidateFileList,
    sortedCondition
  ])

  useEffect(() => {
    if (revalidateFileList) {
      if (revalidateFileList === selectedChildrenFolder) {
        mutateChildrenFolder()
      }
      setRevalidateFileList(undefined)
    }
  }, [
    mutateChildrenFolder,
    revalidateFileList,
    selectedChildrenFolder,
    setRevalidateFileList
  ])

  return (
    <>
      <div className="w-full grid grid-rows-[1fr_auto] overflow-y-auto">
        {uploadFileStep ? (
          <UploadFileMenu
            documentBreadcrumb={selectedFolder}
            handleSwitchMenu={handleSwitchMenu}
            selectedFolder={selectedFolder}
            folderId={selectedChildrenFolder}
            existingFiles={
              selectedChildrenFolder
                ? sortedChildrenFolderFiles
                : sortedBuildingFiles
            }
            allBuildingFiles={allBuildingFiles}
          />
        ) : (
          <>
            <div className="grid grid-rows-[auto_auto_1fr] overflow-auto">
              {selectedFolder?.doc.id !== 1 && (
                <div className="flex items-center gap-3 min-h-[50px] p-[10px] border-0 border-b border-solid theme-border-secondary">
                  <div
                    className="flex gap-3 items-center cursor-pointer"
                    onClick={handleMenuBack}
                  >
                    <div className="flex-shrink-0">
                      <LeftArrowIcon />
                    </div>
                    <Typography className="text-white font-[700] text-[14px] my-auto overflow-hidden">
                      {selectedFolder?.doc?.label}
                      {!!selectedFolder?.subDoc && (
                        <>&nbsp;&gt;&nbsp;{selectedFolder.subDoc.label}</>
                      )}
                      {!isValidatingChildrenFolderFiles &&
                        !!selectedChildrenFolder && (
                          <>&nbsp;&gt;&nbsp;{childrenFolderFiles?.name}</>
                        )}
                    </Typography>
                  </div>
                  {selectedFolder?.doc.id === 41 && !selectedChildrenFolder ? (
                    <div
                      className="w-5 h-5 flex rounded-full theme-bg-sub-primary cursor-pointer ml-auto"
                      onClick={handleOpenMenu}
                    >
                      <AddFolderIcon className="m-auto" />
                    </div>
                  ) : null}
                  {selectedFolder?.doc.id === 41 && selectedChildrenFolder ? (
                    <div
                      className="w-5 h-5 flex rounded-full cursor-pointer ml-auto"
                      onClick={handleOpenMenu}
                    >
                      <MoreIcon className="m-auto" />
                    </div>
                  ) : null}
                </div>
              )}
              {/* new ui */}
              {selectedFolder?.doc.id !== 1 &&
                !isValidating &&
                !isValidatingChildrenFolderFiles && (
                  <div className="flex items-center justify-between px-[12px] py-[8px] border-0 border-b border-solid theme-border-secondary bg-[#1c314d] h-[36px] flex-shrink-0">
                    <div className="flex items-center gap-2 flex-1">
                      <Typography.Text className="text-white text-[12px] whitespace-nowrap">
                        {t('common.sortBy') || 'Sort by'}:
                      </Typography.Text>
                      <ConfigProvider
                        theme={{
                          components: {
                            Select: {
                              colorText: '#ffffff',
                              colorTextPlaceholder: 'rgba(255, 255, 255, 0.5)',
                              colorIcon: '#ffffff',
                              colorTextQuaternary: '#ffffff',
                              colorTextTertiary: '#ffffff',
                              colorBgElevated: '#1c314d',
                              controlItemBgHover: '#2a4669',
                              controlItemBgActive: '#3163b7',
                              colorTextDescription: '#ffffff',
                              fontSize: 12
                            }
                          }
                        }}
                      >
                        <Select
                          className="sort-select-custom flex-1"
                          style={{ width: '100%' }}
                          value={sortBy}
                          onChange={(value) => setSortBy(value)}
                          popupClassName="theme-dropdown"
                          bordered={false}
                          size="small"
                          options={[
                            {
                              value: 'name',
                              label: t('common.name') || 'Name'
                            },
                            {
                              value: 'createdAt',
                              label: t('common.createdAt') || 'Created Date'
                            }
                          ]}
                        />
                      </ConfigProvider>
                    </div>
                    <div
                      className="cursor-pointer text-white hover:theme-text-primary transition-colors p-1 rounded hover:bg-[#ffffff10]"
                      onClick={() =>
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                      }
                    >
                      {sortOrder === 'asc' ? (
                        <SortAscendingOutlined className="text-[16px]" />
                      ) : (
                        <SortDescendingOutlined className="text-[16px]" />
                      )}
                    </div>
                  </div>
                )}
              <div
                className={clsx('overflow-auto relative text-white', {
                  'opacity-50': isValidating || isValidatingChildrenFolderFiles
                })}
              >
                {selectedFolder?.doc.id !== 1 && !selectedChildrenFolder ? (
                  <>
                    {getBuiildingType() !== 20 && (
                      <ChildrenFolderList
                        categoryId={sortedCondition}
                        setSelectedChildrenFolder={setSelectedChildrenFolder}
                      />
                    )}
                    {sortedBuildingFiles.map(
                      (item: BuildingFile, index: number) => (
                        <BuildingFileDetail
                          item={item}
                          handleToggleDeleteFile={handleToggleDeleteFile}
                          handleSetRevalidateByCategory={
                            handleSetRevalidateByCategory
                          }
                          onOpen={() => setPreviewFileIndex(index)}
                          existingNames={sortedBuildingFiles
                            .filter((f) => f.id !== item.id)
                            .map((f) => f.name)}
                          key={item.id}
                        />
                      )
                    )}
                  </>
                ) : null}

                {selectedFolder?.doc.id !== 1 && selectedChildrenFolder
                  ? sortedChildrenFolderFiles.map(
                      (item: BuildingFile, index: number) => (
                        <BuildingFileDetail
                          item={item}
                          handleToggleDeleteFile={handleToggleDeleteFile}
                          handleSetRevalidateByCategory={
                            handleSetRevalidateByCategory
                          }
                          onOpen={() => setPreviewFileIndex(index)}
                          existingNames={sortedChildrenFolderFiles
                            .filter((f) => f.id !== item.id)
                            .map((f) => f.name)}
                          key={item.id}
                        />
                      )
                    )
                  : null}

                {selectedFolder?.doc.id === 1
                  ? sorted360Images.map((item) => (
                      <Image360Detail
                        item={item}
                        key={item.id}
                        handleSelectImage={handleSelectImage}
                        selectImage={selectImage}
                        isInFloor={item.isInFloor}
                        pointOrder={item.pointOrder}
                        confirmDelete={handleToggleDeleteModal}
                      />
                    ))
                  : null}

                {!selectedChildrenFolder && hasMore ? (
                  <div ref={sentinelRef} className="h-px" />
                ) : null}

                {!isValidating &&
                !buildingFiles.files.length &&
                !buildingFiles.folders ? (
                  <Typography className="mt-[14px] text-center text-[13px] leading-[19.5px] text-white">
                    {t('building.fileNotFound')}
                  </Typography>
                ) : null}
              </div>
            </div>

            {(isValidating || isValidatingChildrenFolderFiles) && (
              <Spinner className="!bg-[#00000008]" />
            )}

            <div className="flex h-[3.75rem] border-0 border-t border-solid theme-border-secondary">
              <Button
                type="primary"
                className="mt-[10px] m-auto"
                sizing="h-[40px] w-[160px]"
                onClick={handleSwitchMenu}
              >
                {t('common.upload')}
              </Button>
            </div>
          </>
        )}
      </div>

      {deleteFile && (
        <ModalDeleteFile
          handleToggleDeleteFile={handleToggleDeleteFile}
          handleSetRevalidateByCategory={handleSetRevalidateByCategory}
          item={deleteFile}
          selectedChildrenFolder={selectedChildrenFolder}
        />
      )}

      {confirmDelete && (
        <ModalDelete360Image
          selectedFile={confirmDelete}
          handleToggle={handleToggleDeleteModal}
          handleSetRevalidateByCategory={handleSetRevalidateByCategory}
        />
      )}

      {isAddingFolder && sortedCondition && (
        <ModalAddOrEditChildrenFolder
          categoryId={sortedCondition}
          selectedFolderName={childrenFolderFiles?.name}
          selectedChildrenFolder={selectedChildrenFolder}
          setSelectedChildrenFolder={setSelectedChildrenFolder}
          setIsAddingFolder={setIsAddingFolder}
          revalidate={mutate}
          revalidateChildrenFolder={mutateChildrenFolder}
        />
      )}

      {previewFileIndex !== undefined &&
        (() => {
          const fileList = selectedChildrenFolder
            ? sortedChildrenFolderFiles
            : sortedBuildingFiles
          return (
            <ViewerFilePreview
              fileIndex={previewFileIndex}
              file={fileList[previewFileIndex]}
              maxLength={fileList.length}
              setPreviewFileIndex={setPreviewFileIndex}
            />
          )
        })()}
    </>
  )
}
